import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Play, MapPin, Clock, Filter, Check, Video, Youtube, Share2, Bell, FileText, BarChart2, ChevronDown, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { ShareButtons } from "./ShareButtons";
import { RemindMeButton } from "./RemindMeButton";
import { AdBanner } from "./AdBanner";
import { ASNLoader } from "./ASNLoader";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GameLink = ({ url, isExternal, className, children, ...props }) => {
  if (isExternal) {
    return <a href={url} target="_blank" rel="noopener noreferrer" className={className} {...props}>{children}</a>;
  }
  return <Link to={url} className={className} {...props}>{children}</Link>;
};

export const SchedulePage = () => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [completedBroadcasts, setCompletedBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState("all");
  const [showResults, setShowResults] = useState(false);
  const [liveScores, setLiveScores] = useState({});
  const [offseason, setOffseason] = useState(false);

  // Fetch live scores for all live broadcasts
  const fetchLiveScores = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/broadcasts/live-scores`);
      setLiveScores(res.data || {});
    } catch (error) {
      console.error("Error fetching live scores:", error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [upcomingRes, completedRes, offseasonRes] = await Promise.all([
          axios.get(`${API}/broadcasts/schedule?upcoming_only=true&include_event_info=true`),
          axios.get(`${API}/broadcasts/completed?include_event_info=true`),
          axios.get(`${API}/settings/offseason`).catch(() => ({ data: { enabled: false } }))
        ]);
        setBroadcasts(upcomingRes.data);
        setCompletedBroadcasts(completedRes.data);
        setOffseason(offseasonRes.data?.enabled || false);
      } catch (error) {
        console.error("Error fetching schedule:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Poll for live scores every 30 seconds when there are live broadcasts
  useEffect(() => {
    const hasLiveBroadcasts = broadcasts.some(b => b.is_live);
    if (!hasLiveBroadcasts) return;

    // Fetch immediately
    fetchLiveScores();

    // Then poll every 30 seconds
    const interval = setInterval(fetchLiveScores, 30000);
    return () => clearInterval(interval);
  }, [broadcasts, fetchLiveScores]);

  const sports = ["all", ...new Set([...broadcasts, ...completedBroadcasts].map(b => b.sport).filter(Boolean))];
  
  // Choose which broadcasts to display based on showResults toggle
  const displayBroadcasts = showResults ? completedBroadcasts : broadcasts;
  const filteredBroadcasts = selectedSport === "all" 
    ? displayBroadcasts 
    : displayBroadcasts.filter(b => b.sport === selectedSport);

  // Group broadcasts by date
  const groupedByDate = filteredBroadcasts.reduce((acc, broadcast) => {
    const date = broadcast.scheduled_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(broadcast);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedByDate).sort(showResults ? (a, b) => b.localeCompare(a) : undefined);

  // Get unique months for "Jump to Month" dropdown
  const months = useMemo(() => {
    const monthSet = new Set();
    sortedDates.forEach(dateStr => {
      const date = new Date(dateStr + "T00:00:00");
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthSet.add(monthKey);
    });
    return Array.from(monthSet).sort(showResults ? (a, b) => b.localeCompare(a) : undefined);
  }, [sortedDates, showResults]);

  const formatMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Ref for jump to month functionality
  const monthRefs = useRef({});

  const handleJumpToMonth = (monthKey) => {
    if (monthRefs.current[monthKey]) {
      monthRefs.current[monthKey].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Get month key from date string
  const getMonthKey = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatFullDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "TBD";
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="schedule-page">
      {/* Top Ad Banner */}
      <AdBanner placement="schedule" />
      
      {/* Hero Header */}
      <div className="relative bg-gradient-to-b from-zinc-900 to-[#0a0a0a] border-b border-white/5">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1461896836934-aacd0a1d5b1a?w=1600')] bg-cover bg-center opacity-10"></div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-8 bg-white"></div>
            <span className="text-zinc-400 text-sm font-medium uppercase tracking-widest">Antler Sports Network</span>
          </div>
          <h1 className="font-secondary text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white mb-4" data-testid="schedule-title">
            {showResults ? "Results" : "Schedule"}
          </h1>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <p className="text-zinc-400 text-sm md:text-lg max-w-xl">
              {showResults ? "View completed games with final scores and on-demand recordings" : "Complete broadcast schedule for all upcoming games and events"}
            </p>
            <ShareButtons 
              title={showResults ? "ASN Results - View completed games" : "ASN Schedule - Upcoming broadcasts"} 
              variant="dropdown" 
            />
          </div>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* Controls Bar */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6 pb-4 border-b border-white/10">
          {/* Schedule/Results Toggle */}
          <div className="flex items-center gap-1 bg-zinc-900 border border-white/10 p-1">
            <button
              onClick={() => setShowResults(false)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                !showResults ? 'bg-white text-black' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="schedule-tab"
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Schedule
            </button>
            <button
              onClick={() => setShowResults(true)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                showResults ? 'bg-white text-black' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="results-tab"
            >
              <Check className="w-4 h-4 inline mr-2" />
              Results
            </button>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            {/* Jump to Month Dropdown */}
            {months.length > 1 && (
              <div className="relative">
                <select
                  onChange={(e) => e.target.value && handleJumpToMonth(e.target.value)}
                  className="appearance-none bg-zinc-900 border border-white/10 text-zinc-400 px-4 py-2 pr-8 text-xs font-bold uppercase tracking-wider cursor-pointer hover:border-white/30 focus:outline-none focus:border-white/30"
                  data-testid="jump-to-month-dropdown"
                  defaultValue=""
                >
                  <option value="" disabled>Jump to Month</option>
                  {months.map(monthKey => (
                    <option key={monthKey} value={monthKey}>
                      {formatMonthLabel(monthKey)}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-zinc-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            )}

            {/* Sport Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-4 h-4 text-zinc-500 mr-2" />
              {sports.map(sport => (
                <button
                  key={sport}
                  onClick={() => setSelectedSport(sport)}
                  data-testid={`filter-${sport}`}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                    selectedSport === sport
                      ? 'bg-white text-black'
                      : 'bg-zinc-900 text-zinc-400 hover:text-white border border-white/10 hover:border-white/30'
                  }`}
                >
                  {sport === "all" ? "All Sports" : sport}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* OFFSEASON MODE MESSAGE */}
        {offseason && !showResults && !loading && (
          <div className="text-center py-16 md:py-24" data-testid="offseason-message">
            <div className="max-w-lg mx-auto">
              <div className="w-16 h-16 mx-auto mb-6 bg-zinc-800 rounded-full flex items-center justify-center">
                <Calendar className="w-8 h-8 text-zinc-500" />
              </div>
              <h2 className="text-white font-bold text-xl md:text-2xl uppercase tracking-tight mb-3">
                We're In The Offseason
              </h2>
              <p className="text-zinc-400 text-sm md:text-base mb-2">
                There are currently no games scheduled at this time.
              </p>
              <p className="text-zinc-500 text-sm mb-8">
                Check back soon for upcoming broadcasts, or view past game results using the Results tab above.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-black font-bold text-sm uppercase hover:bg-orange-400 transition-colors"
                  data-testid="offseason-contact-btn"
                >
                  <Mail className="w-4 h-4" />
                  Contact Us
                </Link>
                <button
                  onClick={() => setShowResults(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-800 text-white font-bold text-sm uppercase hover:bg-zinc-700 transition-colors border border-white/10"
                  data-testid="offseason-results-btn"
                >
                  <Check className="w-4 h-4" />
                  View Results
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Content */}
        {offseason && !showResults ? null : loading ? (
          <div className="flex items-center justify-center h-64">
            <ASNLoader size="default" />
          </div>
        ) : sortedDates.length === 0 ? (
          <div className="text-center py-20 bg-zinc-900/30 border border-white/5">
            <Calendar className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="font-secondary text-2xl font-bold text-white mb-2">
              {showResults ? "No Results Yet" : "No Upcoming Events"}
            </h3>
            <p className="text-zinc-500">
              {showResults ? "Completed games with scores will appear here." : "Check back soon for scheduled games and broadcasts."}
            </p>
          </div>
        ) : (
          <div className="space-y-0" data-testid="schedule-list">
            {sortedDates.map((date, dateIndex) => {
              const monthKey = getMonthKey(date);
              const isFirstDateOfMonth = dateIndex === 0 || getMonthKey(sortedDates[dateIndex - 1]) !== monthKey;
              
              return (
                <div key={date} ref={isFirstDateOfMonth ? el => monthRefs.current[monthKey] = el : null}>
                  <div className="border-b border-white/5 last:border-b-0">
                    {/* Date Header */}
                    <div className="bg-zinc-900/50 px-6 py-3 border-l-4 border-white">
                      <h2 className="font-secondary text-base font-bold uppercase tracking-tight text-white" data-testid={`date-header-${date}`}>
                        {formatFullDate(date)}
                      </h2>
                    </div>

                    {/* Games for this date */}
                    <div>
                      {groupedByDate[date].map((broadcast) => (
                        <GameRow 
                          key={broadcast.id} 
                          broadcast={broadcast} 
                          formatTime={formatTime}
                          showResults={showResults}
                          liveScore={liveScores[broadcast.id]}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

const GameRow = ({ broadcast, formatTime, showResults, liveScore }) => {
  const homeTeamObj = typeof broadcast.home_team === 'object' ? broadcast.home_team : null;
  const awayTeamObj = typeof broadcast.away_team === 'object' ? broadcast.away_team : null;
  const homeTeamName = homeTeamObj?.name || (typeof broadcast.home_team === 'string' ? broadcast.home_team : '');
  const awayTeamName = awayTeamObj?.name || (typeof broadcast.away_team === 'string' ? broadcast.away_team : '');
  const hasTeamNames = homeTeamName && awayTeamName;
  
  const isLive = broadcast.is_live;
  const isCompleted = broadcast.is_completed;
  const hasFinalScore = broadcast.final_home_score !== null && broadcast.final_home_score !== undefined;
  const eventInfo = broadcast.event_info;
  
  const hasLiveScore = isLive && liveScore;
  const liveHomeScore = liveScore?.home_score;
  const liveAwayScore = liveScore?.away_score;
  const livePeriod = liveScore?.period;
  const liveClock = liveScore?.clock;
  
  const gameUrl = broadcast.custom_url || `/game/${broadcast.id}`;
  const isExternal = !!broadcast.custom_url;

  // Get abbreviation (max 4 chars)
  const getAbbr = (teamObj, teamName) => {
    if (teamObj?.abbreviation) return teamObj.abbreviation.substring(0, 4).toUpperCase();
    if (!teamName || typeof teamName !== 'string') return "TBD";
    const words = teamName.split(' ').filter(w => w.length > 0);
    if (words.length >= 2) return words.map(w => w[0]).join('').substring(0, 4).toUpperCase();
    return teamName.substring(0, 4).toUpperCase();
  };

  const homeAbbr = getAbbr(homeTeamObj, homeTeamName);
  const awayAbbr = getAbbr(awayTeamObj, awayTeamName);

  return (
    <div
      className={`group border-b border-white/5 last:border-b-0 transition-colors hover:bg-zinc-900/80 ${
        isLive ? 'bg-red-500/5 border-l-2 border-l-red-500' : isCompleted ? 'bg-zinc-900/20' : 'bg-[#0f0f0f]'
      }`}
      data-testid={`game-row-${broadcast.id}`}
    >
      {/* Desktop: Spreadsheet Row */}
      <GameLink url={gameUrl} isExternal={isExternal} className="hidden lg:grid lg:grid-cols-[80px_140px_1fr_50px_1fr_100px_80px] gap-0 items-center px-4 py-3 cursor-pointer" data-testid={`game-link-${broadcast.id}`}>
        {/* Col 1: Time/Status */}
        <div className="flex-shrink-0">
          {isLive ? (
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-red-500 font-bold text-xs uppercase">LIVE</span>
            </div>
          ) : isCompleted ? (
            <span className="text-green-500 font-bold text-xs uppercase">FINAL</span>
          ) : (
            <span className="text-zinc-400 text-sm font-medium">{formatTime(broadcast.scheduled_time)}</span>
          )}
        </div>

        {/* Col 2: Sport Badge */}
        <div className="flex-shrink-0 overflow-hidden pr-2 flex items-center gap-1">
          <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider truncate max-w-full ${
            isLive ? 'bg-red-500/20 text-red-400' : 'bg-zinc-800 text-zinc-400'
          }`} title={broadcast.sport}>
            {broadcast.sport}
          </span>
          {broadcast.is_dummy && (
            <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase bg-violet-500/20 text-violet-400">DEV</span>
          )}
        </div>

        {/* Col 3: Away Team */}
        <div className="flex items-center gap-2 justify-end pr-3 min-w-0">
          <div className="text-right min-w-0 flex-1">
            {eventInfo && (
              <span className="text-[10px] uppercase tracking-wider text-orange-500 font-bold block truncate">{eventInfo.name}</span>
            )}
            <span className="text-white font-semibold text-sm truncate block">{awayTeamName || broadcast.title}</span>
          </div>
          {hasTeamNames && (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 border border-white/10"
              style={{ backgroundColor: awayTeamObj?.color || '#333' }}
            >
              {awayTeamObj?.logo_url ? (
                <img src={awayTeamObj.logo_url} alt="" className="w-5 h-5 object-contain" onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <span className="text-white font-bold text-[10px]">{awayAbbr}</span>
              )}
            </div>
          )}
        </div>

        {/* Col 4: Score / VS */}
        <div className="text-center flex-shrink-0">
          {(hasLiveScore || hasFinalScore) ? (
            <div className="flex items-center justify-center gap-1">
              <span className={`text-lg font-bold ${hasLiveScore ? 'text-red-400' : liveAwayScore > liveHomeScore || broadcast.final_away_score > broadcast.final_home_score ? 'text-white' : 'text-zinc-500'}`}>
                {hasLiveScore ? liveAwayScore : broadcast.final_away_score}
              </span>
              <span className="text-zinc-600 text-xs">-</span>
              <span className={`text-lg font-bold ${hasLiveScore ? 'text-red-400' : liveHomeScore > liveAwayScore || broadcast.final_home_score > broadcast.final_away_score ? 'text-white' : 'text-zinc-500'}`}>
                {hasLiveScore ? liveHomeScore : broadcast.final_home_score}
              </span>
            </div>
          ) : (
            <span className="text-zinc-600 font-bold text-sm">@</span>
          )}
        </div>

        {/* Col 5: Home Team */}
        <div className="flex items-center gap-2 pl-3 min-w-0">
          {hasTeamNames && (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 border border-white/10"
              style={{ backgroundColor: homeTeamObj?.color || '#333' }}
            >
              {homeTeamObj?.logo_url ? (
                <img src={homeTeamObj.logo_url} alt="" className="w-5 h-5 object-contain" onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <span className="text-white font-bold text-[10px]">{homeAbbr}</span>
              )}
            </div>
          )}
          <span className="text-white font-semibold text-sm truncate">{homeTeamName}</span>
        </div>

        {/* Col 6: Live Period/Clock */}
        <div className="text-right flex-shrink-0">
          {hasLiveScore && (livePeriod || liveClock) && (
            <span className="text-xs text-red-400 font-medium">{livePeriod}{livePeriod && liveClock ? ' • ' : ''}{liveClock}</span>
          )}
        </div>

        {/* Col 7: Action Icons */}
        <div className="flex items-center justify-end gap-1 flex-shrink-0" onClick={e => e.preventDefault()}>
          {!isLive && !isCompleted && (
            <RemindMeButton broadcastId={broadcast.id} size="small" />
          )}
          {broadcast.youtube_url && <Youtube className="w-4 h-4 text-red-500" />}
          {broadcast.stats_link && <BarChart2 className="w-4 h-4 text-orange-500" />}
        </div>
      </GameLink>

      {/* Mobile Layout */}
      <GameLink url={gameUrl} isExternal={isExternal} className="lg:hidden flex items-center gap-3 px-4 py-3 cursor-pointer" data-testid={`game-link-mobile-${broadcast.id}`}>
        <div className="flex-shrink-0 w-14">
          {isLive ? (
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-red-500 font-bold text-[10px] uppercase">LIVE</span>
            </div>
          ) : isCompleted ? (
            <span className="text-green-500 font-bold text-[10px] uppercase">FINAL</span>
          ) : (
            <span className="text-zinc-400 text-xs">{formatTime(broadcast.scheduled_time)}</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {hasTeamNames ? (
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-white text-sm font-medium truncate">{awayTeamName}</span>
                {(hasLiveScore || hasFinalScore) && (
                  <span className={`text-sm font-bold tabular-nums ${hasLiveScore ? 'text-red-400' : 'text-zinc-400'}`}>
                    {hasLiveScore ? liveAwayScore : broadcast.final_away_score}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-white text-sm font-medium truncate">{homeTeamName}</span>
                {(hasLiveScore || hasFinalScore) && (
                  <span className={`text-sm font-bold tabular-nums ${hasLiveScore ? 'text-red-400' : 'text-zinc-400'}`}>
                    {hasLiveScore ? liveHomeScore : broadcast.final_home_score}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <span className="text-white text-sm font-medium truncate block">{broadcast.title}</span>
          )}
        </div>

        <div className="flex-shrink-0">
          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${isLive ? 'bg-red-500/20 text-red-400' : 'bg-zinc-800 text-zinc-400'}`}>
            {broadcast.sport}
          </span>
        </div>
      </GameLink>
    </div>
  );
};

export default SchedulePage;
